import { useState, useEffect, useRef } from 'react';
import { Lock } from 'lucide-react';

interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>;
}

export function LockScreen({ onUnlock }: LockScreenProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || busy) return;

    setError('');
    setBusy(true);
    try {
      const ok = await onUnlock(password);
      if (!ok) {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch {
      setError('Failed to unlock. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="applock-screen" data-testid="applock-lock">
      <div className="applock-card">
        <div className="applock-icon-wrap">
          <Lock size={36} strokeWidth={2} />
        </div>
        <h1 className="applock-title">App Locked</h1>
        <p className="applock-description">
          Enter your password to unlock and access the dosing calculator.
        </p>

        <form className="applock-form" onSubmit={handleSubmit}>
          <div className="applock-field">
            <label htmlFor="applock-unlock-password">Password</label>
            <input
              id="applock-unlock-password"
              ref={inputRef}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>

          {error && <p className="applock-error">{error}</p>}

          <button
            type="submit"
            className="applock-btn applock-btn-primary"
            disabled={busy || !password}
          >
            {busy ? 'Unlocking...' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
}
